import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries, getDisplayName } from "@/lib/task-query-utils";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useSearch } from "wouter";
import { useSidebar } from "@/components/ui/sidebar";
import { useBreadcrumbs } from "@/contexts/breadcrumb-context";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  CheckSquare,
  MessageSquare,
  Paperclip,
  PawPrint,
  Link2,
  X,
  CalendarCheck,
  Calendar,
  Settings,
  FileText,
  HelpCircle,
} from "lucide-react";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { LinkedAnimalsTooltip } from "@/components/tasks/linked-animals-tooltip";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { CreateTaskSheet } from "@/components/create-task-sheet";
import { TemplatePicker, type PickerBoard } from "@/components/tasks/template-picker";
import { TaskPreviewPanel } from "@/components/tasks/task-preview-panel";
import { PriorityBadge } from "@/components/tasks/priority-badge";
import type { BoardSummary } from "@/components/tasks/my-boards-grid";
import { usePageTitle } from "@/hooks/use-page-title";

interface MeetingTask {
  id: string;
  boardId: string;
  boardName: string;
  title: string;
  description: string | null;
  statusId: string;
  statusName: string;
  dueDate: string | null;
  priority: string | null;
  source: string | null;
  sourceTemplateName: string | null;
  type: string;
  isTemplate: boolean;
  createdAt: string;
  assigneeUserId: string | null;
  assigneeUsername: string | null;
  assigneeFirstName: string | null;
  assigneeLastName: string | null;
  assigneeAvatarUrl: string | null;
  animalCount: number;
  firstAnimalName: string | null;
  firstAnimalId: string | null;
  firstAnimalSpecies: string | null;
  firstAnimalPhotoUrl: string | null;
  interactionCount: number;
  commentCount: number;
  attachmentCount?: number;
  checklist: { done: number; total: number } | null;
  hasNotes: boolean;
  labels: Array<{ labelId: string; labelName: string; labelColor: string | null }>;
}

interface MyMeetingsResponse {
  tasks: MeetingTask[];
  totalCount: number;
  page: number;
  pageSize: number;
  availableStatuses: string[];
  availableBoards: Array<{ id: string; name: string; userRole: string }>;
}

interface BoardMember {
  userId: string;
  role: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

interface BoardStatus {
  id: string;
  name: string;
  isArchived: boolean;
  sortOrder: number;
}

interface BoardLabel {
  id: string;
  name: string;
  color: string | null;
  isArchived: boolean;
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today;
}

function isToday(dueDate: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dueDate + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  return d.getTime() === today.getTime();
}

function getInitials(name: string | null, username: string | null): string {
  if (name) return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  if (username) return username.slice(0, 2).toUpperCase();
  return "?";
}

function formatMeetingDate(dueDate: string): string {
  const d = new Date(dueDate + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function getDateGroupLabel(dueDate: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dueDate + "T00:00:00");
  d.setHours(0, 0, 0, 0);

  const diffMs = d.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 1 && diffDays <= 7) return "This Week";
  if (diffDays > 7 && diffDays <= 14) return "Next Week";
  if (diffDays < -1 && diffDays >= -7) return "Last Week";
  if (diffDays < -7) return "Past";
  return "Upcoming";
}

type DateGroup = {
  label: string;
  tasks: MeetingTask[];
};

const GROUP_ORDER = [
  "Today",
  "Tomorrow",
  "This Week",
  "Next Week",
  "Upcoming",
  "Yesterday",
  "Last Week",
  "Past",
  "No Date Set",
];

function groupByDate(tasks: MeetingTask[]): DateGroup[] {
  const groups = new Map<string, MeetingTask[]>();

  for (const task of tasks) {
    const label = task.dueDate ? getDateGroupLabel(task.dueDate) : "No Date Set";
    if (!groups.has(label)) {
      groups.set(label, []);
    }
    groups.get(label)!.push(task);
  }

  const result: DateGroup[] = [];
  for (const label of GROUP_ORDER) {
    const groupTasks = groups.get(label);
    if (groupTasks && groupTasks.length > 0) {
      result.push({ label, tasks: groupTasks });
    }
  }
  return result;
}

export default function MeetingsPage() {
  usePageTitle("Meetings");
  const { user } = useAuth();
  const { toast } = useToast();
  const { isMobile } = useSidebar();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const urlParams = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const selectedBoardFilter = urlParams.get("board") || "";
  const selectedTaskId = urlParams.get("taskId") || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((prev) => {
        if (prev !== searchQuery) setPage(1);
        return searchQuery;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [pageSize, setPageSize] = useState(50);
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [createBoardId, setCreateBoardId] = useState<string>("");
  const [createTemplateTitle, setCreateTemplateTitle] = useState<string>("");
  const [createTemplateDescription, setCreateTemplateDescription] = useState<string>("");
  const [createTemplatePriority, setCreateTemplatePriority] = useState<string | null>(null);
  const [createTemplateLabelIds, setCreateTemplateLabelIds] = useState<string[]>([]);
  const [createTemplateId, setCreateTemplateId] = useState<string | null>(null);
  const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);

  useBreadcrumbs([{ label: "Planning" }, { label: "Meetings" }]);

  const { data: summariesData, isLoading: summariesLoading } = useQuery<{ boards: BoardSummary[] }>({
    queryKey: ["/api/task-boards/summaries?type=meeting"],
  });

  const boardSummaries = summariesData?.boards ?? [];

  const canCreate = useMemo(() => {
    if (!user) return false;
    return boardSummaries.some(b =>
      b.members.some(m => m.userId === user.id && (m.role === "admin" || m.role === "editor"))
    ) || user.isAdmin;
  }, [user, boardSummaries]);

  const myMeetingsQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("status", statusFilter);
    if (selectedBoardFilter) params.set("board", selectedBoardFilter);
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    return params.toString();
  }, [statusFilter, selectedBoardFilter, debouncedSearch, page, pageSize]);

  const { data: meetingsData, isLoading: meetingsLoading } = useQuery<MyMeetingsResponse>({
    queryKey: ["/api/tasks/my-meetings?" + myMeetingsQueryParams],
  });

  const tasksList = meetingsData?.tasks ?? [];
  const totalCount = meetingsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const availableStatuses = meetingsData?.availableStatuses ?? [];

  const sortedTasks = useMemo(() => {
    return [...tasksList].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    });
  }, [tasksList]);

  const dateGroups = useMemo(() => groupByDate(sortedTasks), [sortedTasks]);

  const pickerBoards: PickerBoard[] = useMemo(() => {
    if (!user) return [];
    return boardSummaries.map(b => {
      const me = b.members.find(m => m.userId === user.id);
      const role = user.isAdmin ? "admin" : (me?.role ?? "viewer");
      return { id: b.id, name: b.name, userRole: role };
    });
  }, [user, boardSummaries]);

  const creatableBoards = useMemo(() => pickerBoards.filter(b => b.userRole === "admin" || b.userRole === "editor"), [pickerBoards]);
  const resolvedPickerBoardId = useMemo(() => {
    const active = creatableBoards.find(b => b.id === selectedBoardFilter);
    if (active) return active.id;
    if (creatableBoards.length === 1) return creatableBoards[0].id;
    return "";
  }, [creatableBoards, selectedBoardFilter]);

  const { data: pickerTemplatesData, isSuccess: pickerTemplatesLoaded } = useQuery<{ templates: { id: string; title: string; description: string | null; priority: string | null; labelIds: string[] }[] }>({
    queryKey: ["/api/task-boards", resolvedPickerBoardId, "templates"],
    enabled: !!resolvedPickerBoardId,
  });

  const { data: createBoardStatuses } = useQuery<{ statuses: BoardStatus[] }>({
    queryKey: ["/api/task-boards", createBoardId, "statuses"],
    enabled: !!createBoardId && createOpen,
  });

  const { data: createBoardLabels } = useQuery<{ labels: BoardLabel[] }>({
    queryKey: ["/api/task-boards", createBoardId, "labels"],
    enabled: !!createBoardId && createOpen,
  });

  const { data: createBoardMembers } = useQuery<{ members: BoardMember[] }>({
    queryKey: ["/api/tasks/board-members", createBoardId],
    enabled: !!createBoardId && createOpen,
  });

  function handleNewMeeting() {
    if (!pickerTemplatesLoaded || !resolvedPickerBoardId) {
      setPickerOpen(true);
      return;
    }
    const templates = pickerTemplatesData?.templates ?? [];
    if (templates.length === 1) {
      const t = templates[0];
      setCreateBoardId(resolvedPickerBoardId);
      setCreateTemplateId(t.id);
      setCreateTemplateTitle(t.title);
      setCreateTemplateDescription(t.description ?? "");
      setCreateTemplatePriority(t.priority);
      setCreateTemplateLabelIds(t.labelIds ?? []);
      setCreateOpen(true);
    } else {
      setPickerOpen(true);
    }
  }

  function handleTemplateSelect(templateId: string | null, boardId?: string, templateData?: { title: string; description: string | null; priority: string | null; labelIds: string[] }) {
    const targetBoard = boardId || selectedBoardFilter;
    if (targetBoard) {
      setCreateBoardId(targetBoard);
      setCreateTemplateId(templateId);
      setCreateTemplateTitle(templateData?.title ?? "");
      setCreateTemplateDescription(templateData?.description ?? "");
      setCreateTemplatePriority(templateData?.priority ?? null);
      setCreateTemplateLabelIds(templateData?.labelIds ?? []);
      setCreateOpen(true);
    }
  }

  function handleBoardFilter(boardId: string) {
    setPage(1);
    setSearchQuery("");
    setDebouncedSearch("");
    const params = new URLSearchParams();
    if (boardId) params.set("board", boardId);
    if (selectedTaskId) params.set("taskId", selectedTaskId);
    const qs = params.toString();
    setLocation(qs ? `/meetings?${qs}` : "/meetings");
  }

  function clearFilters() {
    setStatusFilter("all");
    setSearchQuery("");
    setDebouncedSearch("");
    setPage(1);
  }

  const hasActiveFilters = statusFilter !== "all" || debouncedSearch.trim() !== "";

  function handleTaskClick(taskId: string) {
    if (isMobile) {
      setLocation(`/meetings/${taskId}`);
    } else {
      const params = new URLSearchParams();
      if (selectedBoardFilter) params.set("board", selectedBoardFilter);
      params.set("taskId", taskId);
      setLocation(`/meetings?${params.toString()}`);
    }
  }

  function handlePanelClose() {
    const params = new URLSearchParams();
    if (selectedBoardFilter) params.set("board", selectedBoardFilter);
    const qs = params.toString();
    setLocation(qs ? `/meetings?${qs}` : "/meetings");
  }

  function handleManageBoard(boardId: string) {
    setLocation(`/admin/tasks?boardId=${boardId}`);
  }

  function getUserRoleForBoard(boardId: string): string | null {
    if (!user) return null;
    if (user.isAdmin) return "admin";
    const board = boardSummaries.find(b => b.id === boardId);
    if (!board) return null;
    const me = board.members.find(m => m.userId === user.id);
    return me?.role ?? null;
  }

  const activeBoardSummary = boardSummaries.find(b => b.id === selectedBoardFilter);

  const totalAll = boardSummaries.reduce((sum, b) => sum + b.totalTaskCount, 0);

  if (!user) return null;

  return (
    <div className="flex flex-col h-full overflow-auto" data-testid="page-meetings">
      <div className="px-4 sm:px-6 pt-4">
        <PageHeader
          title={
            <span className="flex items-center gap-2" data-testid="heading-meetings">
              <Calendar className="w-6 h-6 text-primary" />
              Meetings
            </span>
          }
          actions={canCreate ? (
            <>
              <Button
                data-testid="button-create-meeting"
                onClick={handleNewMeeting}
                className="shrink-0 hidden sm:inline-flex"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Meeting
              </Button>
              <Button
                data-testid="button-create-meeting-mobile"
                onClick={handleNewMeeting}
                size="icon"
                className="shrink-0 sm:hidden"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </>
          ) : undefined}
        />
      </div>
      <div className="sticky top-0 z-40 px-4 sm:px-6 pt-3 pb-3 border-b bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60" data-testid="meetings-header">
        <Card data-testid="filter-card">
          <CardContent className="p-4">
            <CollapsibleFilterHeader
              collapsed={filterCollapsed}
              onToggle={() => setFilterCollapsed(c => !c)}
              description={
                <>
                  {selectedBoardFilter && activeBoardSummary
                    ? `Meetings on ${activeBoardSummary.name}`
                    : "All meetings across all boards"}
                  {statusFilter !== "all" ? `, status: ${statusFilter}` : ""}
                  {debouncedSearch.trim() ? `, search: "${debouncedSearch.trim()}"` : ""}
                </>
              }
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              clearTestId="button-clear-meeting-filters"
              alwaysVisibleContent={filterCollapsed ? (
                <>
                  <div className="hidden sm:flex overflow-x-auto gap-2 p-1 mt-3 scrollbar-thin" data-testid="board-filter-pills">
                    <button
                      className={`shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium transition-shadow ${!selectedBoardFilter ? "ring-2 ring-primary" : "hover-elevate cursor-pointer"}`}
                      onClick={() => handleBoardFilter("")}
                      data-testid="button-filter-all-meetings"
                    >
                      All Meetings
                    </button>
                    {summariesLoading ? (
                      <>
                        <Skeleton className="w-[100px] h-[34px] shrink-0 rounded-md" />
                        <Skeleton className="w-[100px] h-[34px] shrink-0 rounded-md" />
                      </>
                    ) : (
                      boardSummaries.map(board => {
                        const isActive = selectedBoardFilter === board.id;
                        return (
                          <button
                            key={board.id}
                            className={`shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium transition-shadow ${isActive ? "ring-2 ring-primary" : "hover-elevate cursor-pointer"}`}
                            onClick={() => handleBoardFilter(board.id)}
                            data-testid={`button-filter-board-${board.id}`}
                          >
                            {board.name}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <div className="flex sm:hidden mt-3">
                    <Select
                      value={selectedBoardFilter || "__all__"}
                      onValueChange={v => handleBoardFilter(v === "__all__" ? "" : v)}
                    >
                      <SelectTrigger className="w-full" data-testid="select-board-filter-mobile">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__" data-testid="option-board-all-mobile">
                          All Meetings ({totalAll})
                        </SelectItem>
                        {boardSummaries.map(board => (
                          <SelectItem key={board.id} value={board.id} data-testid={`option-board-${board.id}-mobile`}>
                            {board.name} ({board.totalTaskCount})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : undefined}
            >
              <div className="hidden sm:flex overflow-x-auto gap-3 p-1 mt-3 scrollbar-thin" data-testid="board-filter-cards">
                <button
                  className={`min-w-[180px] flex-shrink-0 rounded-md border p-3 text-left transition-shadow ${!selectedBoardFilter ? "ring-2 ring-primary" : "hover-elevate cursor-pointer"}`}
                  onClick={() => handleBoardFilter("")}
                  data-testid="button-filter-all-meetings"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm truncate">All Meetings</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {totalAll} meeting{totalAll !== 1 ? "s" : ""}
                  </div>
                </button>
                {summariesLoading ? (
                  <>
                    <Skeleton className="min-w-[180px] h-[72px] flex-shrink-0 rounded-md" />
                    <Skeleton className="min-w-[180px] h-[72px] flex-shrink-0 rounded-md" />
                  </>
                ) : (
                  boardSummaries.map(board => {
                    const isActive = selectedBoardFilter === board.id;
                    const myRole = getUserRoleForBoard(board.id);
                    const isAdmin = myRole === "admin";
                    const displayMembers = board.members.slice(0, 4);
                    return (
                      <button
                        key={board.id}
                        className={`min-w-[180px] max-w-[260px] flex-shrink-0 rounded-md border p-3 text-left transition-shadow ${isActive ? "ring-2 ring-primary" : "hover-elevate cursor-pointer"}`}
                        onClick={() => handleBoardFilter(board.id)}
                        data-testid={`button-filter-board-${board.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm truncate flex-1">{board.name}</span>
                          {isAdmin && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleManageBoard(board.id); }}
                                  role="button"
                                  tabIndex={0}
                                  data-testid={`button-manage-board-${board.id}`}
                                >
                                  <Settings className="w-3.5 h-3.5" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Board Settings</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs text-muted-foreground">
                            {board.totalTaskCount} meeting{board.totalTaskCount !== 1 ? "s" : ""}
                          </div>
                          {myRole && (
                            <Badge variant="secondary" className="text-[10px] shrink-0" data-testid={`badge-role-${board.id}`}>
                              {myRole}
                            </Badge>
                          )}
                        </div>
                        {displayMembers.length > 0 && (
                          <div className="flex items-center gap-0.5 mt-1.5">
                            {displayMembers.map(m => (
                              <Tooltip key={m.userId}>
                                <TooltipTrigger asChild>
                                  <Avatar className="h-5 w-5">
                                    {m.avatarUrl && (
                                      <AvatarImage src={getAvatarSrc(m.avatarUrl, m.userId)} alt={getDisplayName(m.firstName, m.lastName, m.username)} />
                                    )}
                                    <AvatarFallback className="text-[8px]">
                                      {getInitials(getDisplayName(m.firstName, m.lastName, m.username), m.username)}
                                    </AvatarFallback>
                                  </Avatar>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{getDisplayName(m.firstName, m.lastName, m.username)}</p>
                                </TooltipContent>
                              </Tooltip>
                            ))}
                            {board.members.length > 4 && (
                              <span className="text-[10px] text-muted-foreground ml-0.5">+{board.members.length - 4}</span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>

              <div className="flex sm:hidden mt-3">
                <Select
                  value={selectedBoardFilter || "__all__"}
                  onValueChange={v => handleBoardFilter(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger className="w-full" data-testid="select-board-filter-mobile">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" data-testid="option-board-all-mobile">
                      All Meetings ({totalAll})
                    </SelectItem>
                    {boardSummaries.map(board => (
                      <SelectItem key={board.id} value={board.id} data-testid={`option-board-${board.id}-mobile`}>
                        {board.name} ({board.totalTaskCount})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="input-meeting-search"
                      placeholder="Search meetings, comments, checklists..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]" data-testid="select-meeting-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {availableStatuses.map(s => (
                      <SelectItem key={s} value={s} data-testid={`option-status-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CollapsibleFilterHeader>
          </CardContent>
        </Card>

        {!summariesLoading && !meetingsLoading && (
          <div className="flex flex-wrap items-center gap-2 mt-2 px-1">
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {hasActiveFilters ? "RESULTS" : "SHOWING"}
            </span>
            <span className="text-sm text-muted-foreground">—</span>
            <span className="text-sm text-foreground">
              {totalCount} meeting{totalCount !== 1 ? "s" : ""}
              {selectedBoardFilter && activeBoardSummary ? ` in ${activeBoardSummary.name}` : ""}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex flex-col overflow-auto pb-4 ${selectedTaskId && !isMobile ? "pl-4 sm:pl-6 pr-0" : "px-4 sm:px-6"}`}>
          {summariesLoading || meetingsLoading ? (
            <div className="space-y-3 pt-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : tasksList.length === 0 ? (
            <div className="flex-1 flex items-center justify-center" data-testid="empty-no-meetings">
              {hasActiveFilters ? (
                <SearchEmptyState />
              ) : (
                <div className="text-center space-y-2">
                  <CalendarCheck className="w-12 h-12 text-muted-foreground mx-auto" />
                  <p className="text-lg font-medium text-foreground">No meetings yet</p>
                  <p className="text-sm text-muted-foreground">
                    {canCreate ? "Create your first meeting to get started." : "No meetings have been created."}
                  </p>
                  {canCreate && (
                    <Button
                      data-testid="button-create-first-meeting"
                      onClick={handleNewMeeting}
                      className="mt-2"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New Meeting
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto pt-4 space-y-6">
                {dateGroups.map((group) => (
                  <div key={group.label} data-testid={`meeting-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider" data-testid={`heading-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        {group.label}
                      </h3>
                      <Badge variant="secondary" className="text-xs" data-testid={`badge-group-count-${group.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        {group.tasks.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {group.tasks.map(task => (
                        <Card
                          key={task.id}
                          className={`cursor-pointer transition-colors ${isToday(task.dueDate ?? "") ? "border-primary/40" : ""}`}
                          data-testid={`card-meeting-${task.id}`}
                          onClick={() => handleTaskClick(task.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-medium text-sm truncate" data-testid={`text-meeting-title-${task.id}`}>
                                    {task.title}
                                  </span>
                                  <Badge variant="secondary" className="text-xs shrink-0" data-testid={`badge-meeting-status-${task.id}`}>
                                    {task.statusName}
                                  </Badge>
                                  {!selectedBoardFilter && (
                                    <Badge variant="outline" className="text-xs shrink-0" data-testid={`badge-meeting-board-${task.id}`}>
                                      {task.boardName}
                                    </Badge>
                                  )}
                                  <PriorityBadge priority={task.priority} />
                                  {task.labels.map(l => (
                                    <Badge
                                      key={l.labelId}
                                      variant="outline"
                                      className="text-xs shrink-0"
                                      data-testid={`badge-label-${task.id}-${l.labelId}`}
                                    >
                                      {l.labelColor && (
                                        <span
                                          className="w-2 h-2 rounded-full mr-1 inline-block"
                                          style={{ backgroundColor: l.labelColor }}
                                        />
                                      )}
                                      {l.labelName}
                                    </Badge>
                                  ))}
                                  {task.hasNotes && (
                                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" data-testid={`icon-meeting-notes-${task.id}`} />
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                  {task.dueDate && (
                                    <span
                                      className={`flex items-center gap-1 ${isToday(task.dueDate) ? "text-primary font-medium" : ""}`}
                                      data-testid={`text-meeting-date-${task.id}`}
                                    >
                                      <Calendar className="h-3 w-3" />
                                      {formatMeetingDate(task.dueDate)}
                                    </span>
                                  )}
                                  {task.assigneeUserId && (
                                    <span className="flex items-center gap-1" data-testid={`text-meeting-assignee-${task.id}`}>
                                      <Avatar className="h-4 w-4">
                                        {task.assigneeAvatarUrl && (
                                          <AvatarImage src={getAvatarSrc(task.assigneeAvatarUrl, task.assigneeUserId)} alt={getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername)} />
                                        )}
                                        <AvatarFallback className="text-[8px]">
                                          {getInitials(getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername), task.assigneeUsername)}
                                        </AvatarFallback>
                                      </Avatar>
                                      {getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername)}
                                    </span>
                                  )}
                                  {task.animalCount > 0 && (
                                    <LinkedAnimalsTooltip taskId={task.id} animalCount={task.animalCount}>
                                      <span className="flex items-center gap-1" data-testid={`text-meeting-animals-${task.id}`}>
                                        {task.firstAnimalId ? (
                                          <AnimalAvatar
                                            animalId={task.firstAnimalId}
                                            photoUrl={task.firstAnimalPhotoUrl ?? null}
                                            name={task.firstAnimalName || "Animal"}
                                            species={task.firstAnimalSpecies || "OTHER"}
                                            className="h-5 w-5"
                                          />
                                        ) : (
                                          <PawPrint className="w-3 h-3" />
                                        )}
                                        {task.firstAnimalName || "Animal"}
                                        {task.animalCount > 1 && <span>+{task.animalCount - 1}</span>}
                                      </span>
                                    </LinkedAnimalsTooltip>
                                  )}
                                  {task.interactionCount > 0 && (
                                    <span className="flex items-center gap-0.5" data-testid={`text-meeting-interactions-${task.id}`}>
                                      <Link2 className="w-3 h-3" />{task.interactionCount}
                                    </span>
                                  )}
                                  {task.checklist && task.checklist.total > 0 && (
                                    <span className="flex items-center gap-0.5" data-testid={`text-meeting-checklist-${task.id}`}>
                                      <CheckSquare className="w-3 h-3" />{task.checklist.done}/{task.checklist.total}
                                    </span>
                                  )}
                                  {task.commentCount > 0 && (
                                    <span className="flex items-center gap-0.5" data-testid={`text-meeting-comments-${task.id}`}>
                                      <MessageSquare className="w-3 h-3" />{task.commentCount}
                                    </span>
                                  )}
                                  {(task.attachmentCount ?? 0) > 0 && (
                                    <span className="flex items-center gap-0.5" data-testid={`text-meeting-attachments-${task.id}`}>
                                      <Paperclip className="w-3 h-3" />{task.attachmentCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                total={totalCount}
                pageSize={pageSize}
                startIndex={(page - 1) * pageSize + 1}
                endIndex={Math.min(page * pageSize, totalCount)}
                onPageChange={setPage}
                onPageSizeChange={size => { setPageSize(size); setPage(1); }}
                itemLabel="meetings"
                testIdPrefix="meetings"
                className="border-t"
              />
            </>
          )}
        </div>

        {selectedTaskId && !isMobile && (
          <div className="w-[440px] border-l border-border/40 shrink-0 overflow-y-auto bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]" data-testid="meeting-panel-container">
            <TaskPreviewPanel
              taskId={selectedTaskId}
              boardId={selectedBoardFilter || tasksList.find(t => t.id === selectedTaskId)?.boardId || ""}
              onClose={handlePanelClose}
              fromContext="meetings"
            />
          </div>
        )}
      </div>

      <TemplatePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        boards={pickerBoards}
        activeBoardId={selectedBoardFilter || null}
        onSelect={handleTemplateSelect}
        boardType="meeting"
      />
      {createBoardId && (
        <CreateTaskSheet
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) {
              setCreateBoardId("");
              setCreateTemplateId(null);
              setCreateTemplateTitle("");
              setCreateTemplateDescription("");
              setCreateTemplatePriority(null);
              setCreateTemplateLabelIds([]);
            }
          }}
          boardId={createBoardId}
          statuses={(createBoardStatuses?.statuses ?? []).filter(s => !s.isArchived)}
          labels={(createBoardLabels?.labels ?? []).filter(l => !l.isArchived)}
          members={createBoardMembers?.members ?? []}
          boardType="meeting"
          onCreated={(taskId) => handleTaskClick(taskId)}
          initialTitle={createTemplateTitle}
          initialDescription={createTemplateDescription}
          initialPriority={createTemplatePriority}
          initialLabelIds={createTemplateLabelIds}
          templateId={createTemplateId ?? undefined}
        />
      )}
    </div>
  );
}
